import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_GENERIC_FILTER_PARAMS_QUERY } from '../../queries';
import { ServiceContext } from 'providers/ServiceProvider';
import { Card } from '@totalsoft/rocket-ui';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import GenericFilterParamItem from './GenericFilterParamItem';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_GENERIC_FILTER_PARAMS_READ } from 'constants/rightsCodes';

export const genericFilterParamsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'paramCode',
      caption: 'GenericFilterParam.ParamCode',
      type: 'text'
    },
    {
      name: 'paramName',
      caption: 'GenericFilterParam.ParamName',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'GenericFilterParam.ParamName',
      value: 'ParamName'
    },
    {
      caption: 'GenericFilterParam.ParamCode',
      value: 'ParamCode'
    }
  ],
  pager: {
    orderBy: 'ParamName',
    direction: 'asc'
  }
});

const GenericFilterParamListBrowser = () => {
  const client = useApolloClient();
  const [activeService] = useContext(ServiceContext);
  const { store } = useListBrowserUtils(genericFilterParamsStore);
  const canRead = usePermission(ADMIN_GENERIC_FILTER_PARAMS_READ);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_GENERIC_FILTER_PARAMS_QUERY,
        name: 'pagedGenericFilterParams'
      }
    }),
    []
  );

  const onLanguageChange = useCallback(() => {
    store.refetch();
  }, [store]);

  useLanguageTracker({ onChange: () => onLanguageChange });

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedGenericFilterParams' });
      client.cache.gc();
    }
  }, [activeService, client]);

  const columns = useMemo(
    () => [
      { name: 'paramCode', caption: 'GenericFilterParam.ParamCode' },
      { name: 'paramName', caption: 'GenericFilterParam.ParamName' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      {canRead && (
        <>
          <TableListBrowser
            name='generic-filter-param-browser'
            store={genericFilterParamsStore}
            ItemComponent={GenericFilterParamItem}
            columns={columns}
          />
          <ExportModalComponent store={genericFilterParamsStore} options={fetchingOptions} />
          <ApolloFetchingComponent store={genericFilterParamsStore} options={fetchingOptions} />
        </>
      )}
    </Card>
  );
};

export default GenericFilterParamListBrowser;
